import React, { useMemo } from "react";

import styled from "styled-components/macro";
import { isEmpty } from "lodash";

import Badge from "components/Badge";
import { PreventPropagation } from "components/Events";
import MuiAutocomplete from "@material-ui/lab/Autocomplete";
import { TextField } from "@material-ui/core";

import {
  SaleStatus,
  SaleStatusArchetype,
  SaleStatusColors,
  SaleStatusLabels,
  SaleType,
  SelectableSaleStatuses,
} from "constants/sale";

import { useDispatch, useSelector } from "react-redux";
import {
  getAgentsDeploymentSaleBySaleId,
  getSaleById,
  getSaleStatuses,
  getIsLivestockAgent,
  getActiveRole,
} from "selectors";
import { patchDeploymentSale } from "actions/offline/deploymentSale";
import { getSaleStatus } from "lib/sale";

interface StyledAutoCompleteProps {
  $forGrid?: boolean;
  $isEditingInGrid?: boolean;
}

const StyledAutoComplete = styled(MuiAutocomplete)<StyledAutoCompleteProps>`
  & {
    width: fit-content;
    max-width: 100%;
    // Pin the row height in the grid.
    ${({ $forGrid }) => ($forGrid ? "height: 25px;" : "")}
    // The editor cell sits outside the rest of the aggrid cell dom, and needs padding to align with the rest of the cells.
    ${({ $isEditingInGrid }) => ($isEditingInGrid ? "padding: 0 12px;" : "")}
  }

  .MuiAutocomplete-inputRoot[class*="MuiInput-root"] {
    padding-right: 2px;
    min-width: ${({ $forGrid }) => ($forGrid ? "120px;" : "60px")};
  }
  .MuiInputBase-root {
    padding: 0;

    ${({ $forGrid }) =>
      $forGrid
        ? `
    border-left: none; // Grid has its own border
    border-top: none; // Grid has its own border
    border-bottom: none;
    height: 24px;
    min-height: 24px;`
        : ""}
  }

  // Remove that underline nonsense.
  .MuiInput-underline:before,
  .MuiInput-underline:hover:before,
  .MuiInput-underline:after {
    border-bottom: none !important;
  }
  .MuiAutocomplete-inputRoot[class*="MuiInput-root"] .MuiAutocomplete-input {
    // The input plays havoc with alignment in the grid, where aggrid's edit mode drives the dropdown.
    // In other elements, we want the input focus to drive the dropdown - so the input neesd to "display" and gain focus.
    ${({ $forGrid }) =>
      $forGrid ? "display: none;" : "min-width: 0; padding: 0;"}
  }
  .MuiAutocomplete-popupIndicator {
    padding: 2px 4px;
  }
`;

const BadgeValueWrapper = styled.div`
  max-width: calc(100% - 30px);
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 32px;
  ${({ forGrid }) =>
    forGrid
      ? `height: 24px; 
    padding-top: 1px;
   
    `
      : ""}
`;

const AutoCompleteWrapper = styled.div`
  position: relative;

  .MuiAutocomplete-popper {
    /* force it to live inside this wrapper and align right */
    position: absolute !important;
    right: 0 !important;
    left: auto !important;
    transform: none !important;

    /* sizing: wide but never off-screen */
    min-width: 320px;
    max-width: min(560px, calc(100vw - 24px));
  }

  .MuiAutocomplete-root {
    width: fit-content;
    max-width: 100%;
  }
  .MuiAutocomplete-root .MuiFormControl-root {
    width: fit-content;
    max-width: 100%;
  }

  .MuiAutocomplete-root.MuiInputBase-root {
    padding: 0;
  }

  .MuiAutocomplete-root .MuiAutocomplete-popupIndicator {
    padding: 2px 4px;
  }
`;

interface OptionType {
  label: string;
  value: string;
  archetype: SaleStatusArchetype;
}

interface BadgeValueProps {
  saleStatus?: string;
  data?: OptionType;
  forGrid: boolean;
}

const StyledBadge = styled(Badge)`
  height: ${({ forGrid }) => (forGrid ? "24px" : "28px")};
  margin: ${({ forGrid }) => (forGrid ? "0" : "2px")};
  font-size: ${({ forGrid }) => (forGrid ? "12px" : "14px")};
  cursor: pointer;
`;

const BadgeValue = ({
  data,
  saleStatus,
  forGrid = false,
}: BadgeValueProps): React.JSX.Element => {
  const { label, value, archetype } = data;
  return (
    <BadgeValueWrapper>
      <StyledBadge
        width="100%"
        textAlign="center"
        display={forGrid ? "inline" : "block"}
        margin="auto"
        color={SaleStatusColors[archetype || value || saleStatus]}
        forGrid={forGrid}
      >
        {label}
      </StyledBadge>
    </BadgeValueWrapper>
  );
};

interface DeploymentSalePatch {
  status?: string | null;
  deployment_status_id?: string | null;
}

interface StatusSelectProps {
  saleId: number;
  forGrid: boolean;
  onEditComplete?: () => void;
}

const StatusSelect = (props: StatusSelectProps): React.JSX.Element => {
  const { saleId, forGrid, onEditComplete } = props;

  const livestockSale = useSelector<RootState, Sale>(getSaleById(saleId));
  const saleStatuses = useSelector<RootState, Record<string, SaleStatus>>(
    getSaleStatuses,
  );

  const deploymentSale = useSelector<RootState, DeploymentSale>(
    getAgentsDeploymentSaleBySaleId(saleId),
  );

  const saleStatus = deploymentSale?.status || null;
  const deploymentSaleStatusId = deploymentSale?.deploymentStatusId || null;

  const dispatch = useDispatch();

  const handleSelectChange = (_, option) => {
    const updatedDeploymentSale: DeploymentSalePatch = {};
    // If the option is an enumerated value - we're just setting to a specific status.
    if (SaleStatus[option.value]) {
      updatedDeploymentSale.deployment_status_id = null;
      updatedDeploymentSale.status = option.value;
    } else {
      // Otherwise - it's setting the deployment status, and it will be a uuid.
      // Inject the archetype into the status, too.
      updatedDeploymentSale.deployment_status_id = option.value;
      updatedDeploymentSale.status = saleStatuses[option.value].archetype;
    }

    onEditComplete?.();

    dispatch(
      patchDeploymentSale(
        updatedDeploymentSale,
        deploymentSale.deployment_sale_id,
        livestockSale.livestocksale_id,
      ),
    );
  };

  const options = useMemo<OptionType[]>(() => {
    const collectedOptions: OptionType[] = [];

    // Go through the selectable status archetypes - some are NOT selectable (eg, past, futuere, reference)
    SelectableSaleStatuses.forEach(status => {
      // If we have any status' of that type in our custom statuses - show them.
      const customStatuses = Object.values(saleStatuses)
        .filter(
          customStatus =>
            customStatus.archetype === status &&
            (!customStatus.saleType ||
              customStatus.saleType === livestockSale.sale_type ||
              customStatus.id === deploymentSaleStatusId),
        )
        .sort((a, b) => a.name.localeCompare(b.name));
      // If we don't have a custom status for this archetype
      // Or the current value is an existing archetype status, without a custom status - add it to the options.
      if (
        customStatuses.length === 0 ||
        (saleStatus === status && !deploymentSaleStatusId)
      ) {
        collectedOptions.push({
          value: status,
          label: SaleStatusLabels[status],
          archetype: status,
        });
      }

      // Add the custom statuses to the options.
      if (customStatuses.length > 0) {
        customStatuses.forEach(customStatus => {
          collectedOptions.push({
            value: customStatus.id,
            label: customStatus.name,
            archetype: customStatus.archetype,
          });
        });
      }
    });
    return collectedOptions;
  }, [
    saleStatuses,
    saleStatus,
    deploymentSaleStatusId,
    livestockSale.sale_type,
  ]);

  const selectedOption = options.find(
    o => o.value === deploymentSaleStatusId || o.value === saleStatus,
  );

  const isEditingInGrid = Boolean(onEditComplete);
  // Never open in grid, unless we've got the editing overlay enabled.
  const isOpen = forGrid ? isEditingInGrid : undefined;

  return (
    <PreventPropagation>
      <AutoCompleteWrapper>
        <StyledAutoComplete
          // Styling.
          $forGrid={forGrid}
          $isEditingInGrid={isEditingInGrid}
          className="ag-custom-component-popup"
          // Standard MUI props.
          options={options}
          value={selectedOption}
          onChange={handleSelectChange}
          getOptionLabel={o => o?.label || ""}
          disableClearable
          autoHighlight
          disablePortal
          openOnFocus
          blurOnSelect
          open={isOpen}
          onBlur={onEditComplete || undefined}
          onClose={onEditComplete || undefined}
          renderInput={params => (
            <TextField
              {...params}
              InputProps={{
                ...params.InputProps,
                readOnly: true,
                value: "",
                startAdornment: selectedOption ? (
                  <BadgeValue
                    saleStatus={saleStatus}
                    data={selectedOption}
                    forGrid={forGrid}
                  />
                ) : null,
              }}
              // They are, though!  Even in the type it's declared. Different casing.

              inputProps={{
                ...params.inputProps,
                value: "",
              }}
            />
          )}
        />
      </AutoCompleteWrapper>
    </PreventPropagation>
  );
};

interface SaleStatusBadgeProps {
  saleId: number;
  forGrid: boolean;
  editMode: boolean;
}

export function SaleStatusBadge({
  saleId,
  forGrid,
  editMode,
}: SaleStatusBadgeProps): React.JSX.Element {
  const livestockSale = useSelector<RootState, Sale>(getSaleById(saleId));

  const activeRole = useSelector(getActiveRole);

  const saleStatus = getSaleStatus(livestockSale, activeRole);

  // Even if it's read only, aggrid still bunts us into edit mode, which messes with where the whole cell sits.
  // This makes it a little less janky
  const style = editMode ? { margin: "0 10px" } : undefined;

  return (
    <Badge
      display={forGrid && !editMode ? "inline" : "inline-block"}
      margin="auto"
      height={forGrid ? 24 : undefined}
      color={SaleStatusColors[saleStatus]}
      style={style}
    >
      {SaleStatusLabels[saleStatus]}
    </Badge>
  );
}

interface SaleStatusComponentProps {
  saleId: number;
  forGrid?: boolean;
  onEditComplete?: () => void;
}

export function SaleStatusComponent({
  saleId,
  forGrid = false,
  onEditComplete,
}: SaleStatusComponentProps): React.JSX.Element {
  const isLivestockAgent = useSelector(getIsLivestockAgent);

  const livestockSale = useSelector<RootState, Sale>(getSaleById(saleId));
  const deploymentSale = useSelector<RootState, DeploymentSale>(
    getAgentsDeploymentSaleBySaleId(saleId),
  );
  const saleStatuses = useSelector<RootState, SaleStatus>(getSaleStatuses);

  const isConsigningLivestockAgent =
    isLivestockAgent && isEmpty(deploymentSale);

  const isReferenceSale = livestockSale.sale_type === SaleType.REFERENCE_SALE;

  // Show the generic label if the deployment status is custom, and I can't see that custom.
  const canSeeDeploymentStatus = Boolean(
    !deploymentSale.deploymentStatusId ||
      saleStatuses[deploymentSale.deploymentStatusId],
  );

  if (
    isLivestockAgent &&
    !isConsigningLivestockAgent &&
    !isReferenceSale &&
    canSeeDeploymentStatus
  ) {
    return (
      <StatusSelect
        saleId={saleId}
        forGrid={forGrid}
        onEditComplete={onEditComplete}
      />
    );
  }
  // A generic view from "The outside" of a sale status - only uses the status archetype.
  return (
    <SaleStatusBadge
      saleId={saleId}
      forGrid={forGrid}
      editMode={Boolean(onEditComplete)}
    />
  );
}
